import { apiGet, apiPost } from '../client'

export type UserRole = 'STUDENT' | 'EMPLOYER' | 'IMMIGRATION_LAWYER' | 'SCHOOL_ADMIN' | 'SUPER_ADMIN'

export interface LoginResponse {
  email: string
  nextStep: 'enter-totp' | 'verify-otp'
}

export interface VerifyOtpResponse {
  accessToken: string
  isNewUser: boolean
  loginMethod?: 'recovery'
  nextStep: 'select-registration-flow' | 'select-role' | null
  refreshToken: string
  role: UserRole | null
  userId: string
}

export interface RegistrationStatusResponse {
  isRegistered: boolean
}

export interface RefreshTokenResponse {
  accessToken: string
}

export interface AuthenticatedUser {
  [key: string]: unknown
  email: string
  employerProfileId?: string
  id: string
  institutionId?: string
  lawyerProfileId?: string
  role: UserRole
  studentProfileId?: string
}

export interface Institution {
  id: string
  name: string
}

export interface StudentRegisterPayload {
  email: string
  institutionId?: string
}

export interface StudentOnboardingPayload {
  degreeProgram: string
  email: string
  expectedGraduationDate: string
  firstName: string
  institutionId?: string
  lastName: string
  major?: string
  phoneNumber: string
  /** Captured from `?ref=` on the sign-up URL. Fails silently server-side if
   * missing/invalid - never blocks registration. */
  referralCode?: string
  visaStatus: string
}

export interface RequestUniversityPayload {
  fullName: string
  schoolContactEmail: string
  schoolName: string
  studentEmail: string
}

export const login = (email: string) => apiPost<LoginResponse>('/auth/login', { email })

export const verifyLoginOtp = (payload: { email: string; otp: string }) => apiPost<VerifyOtpResponse>('/auth/verify-login-otp', payload)

/** `code` is either a live 6-digit authenticator code or a single-use backup code. */
export const verifyLoginTotp = (payload: { code: string; email: string }) => apiPost<VerifyOtpResponse>('/auth/verify-login-totp', payload)

/** `token` is a single-use recovery credential issued by a Super Admin. */
export const recoveryLogin = (payload: { email: string; token: string }) => apiPost<VerifyOtpResponse>('/auth/recovery-login', payload)

export const resendLoginOtp = (email: string) => apiPost<void>('/auth/resend-login-otp', { email })

export const registrationStatus = (email: string) => apiPost<RegistrationStatusResponse>('/auth/registration-status', { email })

export const refreshToken = (token: string) => apiPost<RefreshTokenResponse>('/auth/refresh-token', { refreshToken: token })

export const logout = () => apiPost<void>('/auth/logout', {})

export const getMe = () => apiGet<AuthenticatedUser>('/auth/me')

export const selectRole = (payload: { companyName?: string; institutionId?: string; role: UserRole }) => apiPost<VerifyOtpResponse>('/auth/select-role', payload)

export const googleTokenExchange = (idToken: string) => apiPost<VerifyOtpResponse>('/auth/google/token', { idToken })

export const requestEmailChange = (newEmail: string) => apiPost<{ newEmail: string }>('/auth/change-email/request', { newEmail })

export const confirmEmailChange = (otp: string) => apiPost<{ email: string }>('/auth/change-email/confirm', { otp })

export const linkedinTokenExchange = (accessToken: string) => apiPost<VerifyOtpResponse>('/auth/linkedin/token', { accessToken })

export const studentRegister = (payload: StudentRegisterPayload) => apiPost<void>('/auth/student/register', payload)

export interface ResolvedStudentInvite {
  email: string
  institutionId: string | null
  institutionName: string | null
}

export const resolveStudentInvite = (token: string) => apiGet<ResolvedStudentInvite>(`/auth/student/invite/${token}`)

export const studentVerifyOtp = (payload: { email: string; otp: string }) => apiPost<VerifyOtpResponse>('/auth/student/verify-otp', payload)

export const studentResendOtp = (email: string) => apiPost<void>('/auth/student/resend-otp', { email })

export const studentOnboarding = (payload: StudentOnboardingPayload) => apiPost<VerifyOtpResponse>('/auth/student/onboarding', payload)

// schools only (no companies or law firms); the backend pages at 20 unless asked, and allows up to 100
export const studentSchools = () => apiGet<Institution[]>('/auth/student/schools', { params: { limit: 100 } })

export const requestUniversity = (payload: RequestUniversityPayload) => apiPost<void>('/auth/student/request-university', payload)
